import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Ship, Calendar, Users, Check, Loader2, FileDown, MapPin, Clock, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BRAND = { burgundy: '#a11a3c', dark: '#423e41', charcoal: '#2d2a2e' };
const BRAND_COLORS = ['#a11a3c', '#e50000', '#008000', '#66cc00', '#3333cc'];

function formatDateFr(d) {
  if (!d) return '';
  const p = d.split('-');
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d;
}

export default function AgencyDevisSignPage() {
  const { signToken } = useParams();
  const [devis, setDevis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showSignPad, setShowSignPad] = useState(false);
  const [cgvAccepted, setCgvAccepted] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);

  useEffect(() => {
    axios.get(`${API}/agency/devis/public/${signToken}`)
      .then(r => { setDevis(r.data); if (r.data.status === 'signed') setSigned(true); })
      .catch(() => setError('Ce lien de devis est invalide ou a expiré.'))
      .finally(() => setLoading(false));
  }, [signToken]);

  // Canvas drawing
  const startDraw = (e) => {
    drawing.current = true;
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext('2d');
    const rect = canvasRef.current.getBoundingClientRect();
    const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const y = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.strokeStyle = BRAND.dark;
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.stroke();
  };
  const endDraw = () => { drawing.current = false; };
  const clearCanvas = () => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
  };

  const handleSign = async () => {
    const data = canvasRef.current.toDataURL();
    // Check if canvas is blank
    const blank = document.createElement('canvas');
    blank.width = canvasRef.current.width;
    blank.height = canvasRef.current.height;
    if (data === blank.toDataURL()) { alert('Veuillez signer avant de valider.'); return; }
    if (!cgvAccepted) { alert('Vous devez accepter les Conditions Générales de Vente avant de valider votre signature.'); return; }

    setSigning(true);
    try {
      await axios.post(`${API}/agency/devis/public/${signToken}/sign`, {
        signature_data: data,
        signed_by: devis.client_name,
      });
      setSigned(true);
    } catch (e) {
      alert(e.response?.data?.detail || 'Erreur lors de la signature');
    } finally { setSigning(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-8 w-8 animate-spin text-[#a11a3c]" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <div className="text-center max-w-md">
        <Ship className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Lien invalide</h1>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  const dv = devis;
  const schedule = dv.payment_schedule || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Brand stripe */}
      <div className="h-1 flex">
        {BRAND_COLORS.map((c, i) => <div key={i} className="flex-1" style={{ background: c }} />)}
      </div>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold" style={{ color: BRAND.dark }}>{dv.agency_name || 'FONTANA TOURISME'}</h1>
          <p className="text-sm mt-1" style={{ color: BRAND.burgundy }}>Agences de voyages</p>
          <p className="text-xs text-gray-400 mt-2">Devis {dv.devis_number}</p>
        </div>

        {signed ? (
          <div className="bg-white rounded-2xl border-2 border-emerald-200 p-8 text-center" data-testid="devis-signed-confirmation">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-bold text-emerald-700 mb-2">Devis accepté !</h2>
            <p className="text-gray-500">Merci {dv.client_name}. Votre devis <b>{dv.devis_number}</b> a été signé avec succès.</p>
            <p className="text-gray-400 text-sm mt-3">Votre agence {dv.agency_name} vous recontactera pour les prochaines étapes.</p>
          </div>
        ) : (
          <>
            {/* Cruise info card */}
            <div className="bg-white rounded-2xl border overflow-hidden mb-6" data-testid="devis-cruise-info">
              <div className="p-6" style={{ background: BRAND.charcoal }}>
                <h2 className="text-white font-bold text-xl">{dv.cruise_name}</h2>
                <div className="flex flex-wrap gap-4 mt-3 text-white/60 text-sm">
                  <span className="flex items-center gap-1"><Calendar className="h-4 w-4" /> {formatDateFr(dv.start_date)} → {formatDateFr(dv.end_date)}</span>
                  <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> {dv.duration}</span>
                  <span className="flex items-center gap-1"><MapPin className="h-4 w-4" /> {dv.departure_port}</span>
                  <span className="flex items-center gap-1"><Users className="h-4 w-4" /> {dv.passenger_count} passager{dv.passenger_count > 1 ? 's' : ''}</span>
                  <span className="flex items-center gap-1"><Ship className="h-4 w-4" /> SUNREEF 50</span>
                </div>
              </div>

              {dv.intro_text && (
                <div className="px-6 pt-5">
                  <p className="text-gray-600 italic border-l-3 pl-3" style={{ borderLeftColor: BRAND.burgundy }}>{dv.intro_text}</p>
                </div>
              )}

              {/* Pricing */}
              <div className="p-6 space-y-3">
                <h3 className="font-semibold text-gray-900">Tarification</h3>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Croisière ({dv.passenger_count} passager{dv.passenger_count > 1 ? 's' : ''})</span>
                    <span className="font-medium">{dv.base_cost}€</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Caisse de bord ({dv.caisse_de_bord_per_person}€/pers.)</span>
                    <span className="font-medium">{dv.caisse_total}€</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-bold" style={{ color: BRAND.dark }}>TOTAL TTC</span>
                    <span className="font-bold text-xl" style={{ color: BRAND.burgundy }}>{dv.total}€</span>
                  </div>
                </div>

                {/* Payment schedule */}
                {schedule.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Échéancier de paiement</h4>
                    <div className="space-y-1">
                      {schedule.map((s, i) => (
                        <div key={i} className="flex justify-between text-sm py-1.5 px-3 bg-gray-50 rounded">
                          <span className="text-gray-500">{s.label}</span>
                          <span className="font-medium">{s.amount}€</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Highlights */}
            {dv.highlights?.length > 0 && (
              <div className="bg-white rounded-2xl border p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-3">Temps forts</h3>
                <div className="flex flex-wrap gap-2">
                  {dv.highlights.map((h, i) => (
                    <span key={i} className="px-3 py-1 text-sm rounded-full" style={{ background: '#f0f9f0', color: '#008000' }}>{h}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Inclusions */}
            {(dv.inclusions?.length > 0 || dv.exclusions?.length > 0) && (
              <div className="bg-white rounded-2xl border p-6 mb-6">
                <div className="grid md:grid-cols-2 gap-6">
                  {dv.inclusions?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Inclus</h4>
                      <ul className="space-y-1">
                        {dv.inclusions.map((item, i) => (
                          <li key={i} className="text-sm text-gray-600 flex items-start gap-2">
                            <Check className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {dv.exclusions?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-gray-700 mb-2">Non inclus</h4>
                      <ul className="space-y-1">
                        {dv.exclusions.map((item, i) => (
                          <li key={i} className="text-sm text-gray-500 flex items-start gap-2">
                            <span className="text-red-400 mt-0.5 shrink-0">✗</span>{item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* CGV Section (collapsible) */}
            <CGVSection />

            {/* Sign section */}
            <div className="bg-white rounded-2xl border p-6" data-testid="devis-sign-section">
              {!showSignPad ? (
                <div className="text-center">
                  <h3 className="font-bold text-gray-900 text-lg mb-2">Accepter ce devis</h3>
                  <p className="text-gray-500 text-sm mb-4">En signant, vous acceptez les termes de ce devis et les conditions générales de vente ci-dessus.</p>
                  <Button onClick={() => setShowSignPad(true)} className="h-12 px-8" style={{ background: BRAND.burgundy }}
                    data-testid="devis-show-sign-btn">
                    Signer et accepter le devis
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <h3 className="font-bold text-gray-900">Votre signature</h3>
                  <p className="text-sm text-gray-500">Signez dans le cadre ci-dessous avec votre souris ou votre doigt :</p>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden">
                    <canvas ref={canvasRef} width={600} height={200}
                      className="w-full bg-white cursor-crosshair touch-none"
                      onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                      onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                      data-testid="devis-sign-canvas" />
                  </div>
                  <div className="flex gap-3">
                    <Button variant="outline" onClick={clearCanvas} className="flex-1" data-testid="devis-clear-sign">Effacer</Button>
                  </div>

                  {/* Mandatory CGV acceptance */}
                  <label className="flex items-start gap-2.5 text-sm leading-relaxed cursor-pointer select-none p-3.5 rounded-xl bg-orange-50 border border-orange-200 hover:border-orange-400 transition-colors" data-testid="agency-cgv-checkbox-label">
                    <input
                      type="checkbox"
                      checked={cgvAccepted}
                      onChange={e => setCgvAccepted(e.target.checked)}
                      className="mt-0.5 w-5 h-5 shrink-0 cursor-pointer"
                      style={{ accentColor: BRAND.burgundy }}
                      data-testid="agency-cgv-checkbox"
                    />
                    <span className="text-gray-700">
                      J'ai lu et j'accepte les <strong>Conditions Générales de Vente</strong> de SOGNUDIMARE (présentées au-dessus). <span className="text-orange-700 font-bold">*</span>
                    </span>
                  </label>

                  <Button onClick={handleSign} disabled={signing || !cgvAccepted} className="w-full disabled:opacity-50 disabled:cursor-not-allowed" style={{ background: BRAND.burgundy }}
                    data-testid="devis-confirm-sign">
                    {signing ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4 mr-1" /> Valider ma signature</>}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer */}
        <div className="mt-8 text-center">
          <div className="h-1 flex rounded-full overflow-hidden mb-4">
            {BRAND_COLORS.map((c, i) => <div key={i} className="flex-1" style={{ background: c }} />)}
          </div>
          <p className="text-xs text-gray-400">
            {dv.agency_name || 'FONTANA TOURISME'} — Agences de voyages
          </p>
          <p className="text-xs text-gray-300 mt-1">contact@fontana-tourisme.com | www.fontana-tourisme.com</p>
        </div>
      </div>
    </div>
  );
}


const CGV_SECTIONS = [
  { title: "1. Prestation", content: "Le type ou la catégorie de navire, ses particularités, le contenu de la prestation, la composition de l'équipage, sont ceux désignés sur le bon de commande. Le prix comprend sauf mentions contraires : mise à disposition du navire, équipage, assurances, carburants, 3 repas/jour et boissons, transferts aéroport/port. Le prix ne comprend pas : assurances personnelles, dépenses personnelles et à terre." },
  { title: "2. Caisse de bord", content: "En sus du prix de la croisière, les passagers devront acquitter une « caisse de bord » forfaitaire et obligatoire (taxes de séjour, droits portuaires, clearances). Aucun supplément ne peut être demandé et aucun remboursement n'est effectué." },
  { title: "3. Restauration", content: "Les repas principaux sont servis à table ou sous forme de buffet. L'Armateur ne peut être tenu responsable de l'impossibilité de satisfaire aux demandes de régimes spéciaux (casher, halal, végétarien, prescriptions médicales...). Toutefois, des dispositions peuvent être tentées pour certaines demandes formulées au minimum 15 jours à l'avance." },
  { title: "4. Boissons incluses", content: "Vins, rhum, cocktails, bières, jus de fruits, cafés, thés, infusions, lait, sodas. La fourniture d'alcool, bien qu'incluse, reste soumise à un usage raisonnable. Le Capitaine est en devoir de refuser des boissons alcoolisées à une personne manifestement en état d'ébriété." },
  { title: "5. Confort et cabines", content: "Attribution des cabines par ordre d'inscription. Bagages limités à des sacs souples. Le Capitaine décline toute responsabilité en cas de vol ou perte d'objets de valeur." },
  { title: "6. Embarquement", content: "Cartes d'embarquement envoyées par email 3 jours avant. En cas de présentation tardive, le Capitaine appareillera sans dédommagement." },
  { title: "7. Itinéraires et conditions météorologiques", content: "Les itinéraires, temps de parcours et escales sont donnés à titre indicatif et peuvent être modifiés en fonction d'impératifs météorologiques. Le Capitaine est le seul maître à bord et dispose d'une autorité absolue sur les décisions de navigation afin d'assurer la sécurité des passagers. Les modifications de programme liées aux conditions météorologiques (changement d'itinéraire, annulation d'escale, réduction du temps de navigation) ne donnent lieu à aucun remboursement, indemnisation ou compensation financière.\n\nEngagement SOGNUDIMARE — Adaptation au changement climatique : En cas de conditions météorologiques difficiles annoncées avant l'embarquement (alertes Météo-France, vents soutenus supérieurs à Force 7, mer dangereuse, alerte de sécurité significative), le Capitaine et l'Armateur SOGNUDIMARE pourront proposer aux passagers de reporter la croisière jusqu'à 12 mois sur une croisière équivalente, sous réserve de disponibilité et sans frais supplémentaires. SOGNUDIMARE est l'un des rares croisiéristes à s'adapter activement aux changements climatiques afin de garantir à ses passagers une expérience sécurisée, confortable et de qualité." },
  { title: "8. Retards de transport et ponctualité", content: "Le Capitaine et l'Armateur SOGNUDIMARE ne peuvent en aucun cas être tenus responsables des retards de transport des passagers, notamment les retards ou annulations de vols, de trains, de ferries ou de tout autre moyen d'acheminement. Les passagers sont seuls responsables de leur ponctualité aux heures d'embarquement. En cas de retard, le Capitaine pourra décider d'appareiller sans attendre, sans qu'aucun remboursement ou indemnisation ne soit dû. Il est fortement recommandé de prévoir des marges horaires suffisantes et de souscrire une assurance voyage." },
  { title: "9. Sécurité", content: "Les passagers s'engagent à respecter les règles de sécurité, assister au briefing et signer le compte rendu. L'embarquement d'armes ou substances illicites est strictement interdit." },
  { title: "10. Refus d'embarquement / débarquement anticipé", content: "Le Capitaine refusera d'embarquer les passagers non conformes aux réglementations, les enfants de moins de 7 ans non signalés, les personnes dont le handicap n'aurait pas été signalé. Le Capitaine débarquera tout passager dont le comportement pourrait mettre en péril les autres passagers." },
  { title: "11. Annulation / Modification", content: "Pas de droit de rétractation. Frais d'annulation : jusqu'à 90 jours : 25%, 89-60 jours : 50%, 59-30 jours : 75%, moins de 30 jours : 100%. Navires en exclusivité : majoration de 20%. La signature du devis ou le paiement d'un acompte engagent définitivement le client. Assurance voyage vivement recommandée." },
  { title: "12. Force majeure", content: "Engagement SOGNUDIMARE : Dans tous les cas de force majeure listés ci-dessous, le report de la croisière jusqu'à 12 mois sera systématiquement établi pour les clients, sur une croisière équivalente, sans aucun frais supplémentaire et sous réserve de disponibilité. Sont considérés comme cas de force majeure dégageant l'Armateur de toute responsabilité (liste non exhaustive) : (1) état de santé ou incapacité du Capitaine ou d'un membre de l'équipage rendant impossible la conduite du navire en sécurité ; (2) conditions météorologiques dangereuses pour la navigation (tempête, vents soutenus > Force 7, alerte Météo-France, houle dangereuse, brouillard, orages violents) ; (3) avarie technique majeure du navire ; (4) catastrophes naturelles ou risques environnementaux (incendies, séismes, tsunamis, marées noires, pollutions, prolifération d'algues toxiques) ; (5) décisions des autorités publiques (fermeture de port, restriction de navigation, mesures sanitaires) ; (6) épidémies, pandémies, crises sanitaires ; (7) grèves, mouvements sociaux, manifestations, blocus, invasion ; (8) actes de guerre, terrorisme, piraterie ; (9) décisions impératives de l'autorité portuaire ; (10) cas fortuit imprévisible et insurmontable (feu, explosion, sabotage, collision, échouage non imputable au Capitaine). La priorité absolue est la sécurité des passagers et de l'équipage. Le report jusqu'à 12 mois constitue la compensation principale. À défaut d'acceptation du report par le client, le remboursement des sommes versées sera effectué dans un délai maximum de dix (10) mois à compter de la notification écrite de l'annulation. Cette modalité, expressément acceptée par le client à la signature du contrat, est justifiée par le caractère irrévocable et non-restituable des charges d'exploitation engagées par l'Armateur préalablement à la croisière (affrètement, frais portuaires, salaires de l'équipage, primes d'assurance, provisions de bord, taxes) et par la nécessité de préserver la trésorerie d'exploitation. Aucune indemnisation financière complémentaire ne pourra être réclamée au-delà." },
  { title: "13. Substitution de navire", content: "L'Armateur se réserve expressément le droit, à tout moment avant ou pendant la croisière, de substituer au navire initialement prévu un navire de remplacement présentant des caractéristiques générales équivalentes ou supérieures (capacité, sécurité, services). La flotte SOGNUDIMARE étant composée de plusieurs catamarans LAGOON 46, 43 et 38 ainsi que de navires partenaires affrétés, la disponibilité d'un navire spécifique ne peut être garantie de manière absolue à la signature du présent contrat ni à toute date antérieure au départ, notamment en raison : (1) d'aléas techniques imprévisibles (avarie moteur, problème de gréement, panne électrique majeure) ; (2) de décisions d'expertise maritime (immobilisation pour maintenance ou réparation) ; (3) d'opérations de mise en sécurité ou de contraintes d'assurance ; (4) de cas de force majeure ; (5) de retards de retour ou d'indisponibilité prolongée d'un navire affrété par un précédent client. La substitution éventuelle ne donne lieu à aucune indemnisation, ne constitue pas une rupture du contrat et ne peut motiver une annulation de la part du Client, dès lors que le navire de remplacement permet l'exécution substantielle des prestations (nombre de cabines, équipage, itinéraire, durée, services, tarif). Le Client renonce à toute réclamation fondée sur la seule identification d'un modèle ou d'un nom de navire dans la documentation, ces éléments étant fournis à titre indicatif et non substantiel. En cas d'impossibilité totale de fourniture d'un navire équivalent, les dispositions de l'Article 12 (Force majeure) s'appliquent." },
  { title: "14. Litiges", content: "Tribunaux de Paris compétents. Loi française applicable. Réclamation sous 30 jours après débarquement." },
];

function CGVSection() {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl border overflow-hidden mb-6" data-testid="devis-cgv-section">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors">
        <div>
          <h3 className="font-semibold text-gray-900">Conditions Générales de Vente</h3>
          <p className="text-xs text-gray-400 mt-0.5">FONTANA TOURISME — Agence distributrice | SOGNUDIMARE — Armateur organisateur</p>
        </div>
        {open ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t">
          <p className="text-xs text-gray-500 italic mt-4 mb-4">
            Les présentes conditions régissent les ventes de croisières distribuées par FONTANA TOURISME
            et opérées par l'Armateur SOGNUDIMARE. Toute réservation implique l'acceptation intégrale de ces CGV.
          </p>
          <div className="space-y-4">
            {CGV_SECTIONS.map((s, i) => (
              <div key={i}>
                <h4 className="text-sm font-semibold" style={{ color: '#a11a3c' }}>{s.title}</h4>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">{s.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
